import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";
import { fmtDate } from "./shared";
import { Home, User, Phone, Mail, MapPin, Ruler, Target, Check, X, ChevronRight, ShoppingCart, ClipboardList, MessageSquare, AlertCircle, Sparkles, Calculator, FileDown, Loader2 } from "lucide-react";

const STATUS_META = {
    nuovo: { label: "Nuovo", cls: "bg-ink-900 text-ink-300 border-white/15" },
    assegnato: { label: "Assegnato", cls: "bg-gold/20 text-gold border-gold/40" },
    contattato: { label: "Contattato", cls: "bg-blue-900/30 text-blue-200 border-blue-700/40" },
    in_trattativa: { label: "In trattativa", cls: "bg-purple-900/30 text-purple-200 border-purple-700/40" },
    chiuso: { label: "Chiuso", cls: "bg-green-900/30 text-green-300 border-green-700/40" },
    perso: { label: "Perso", cls: "bg-red-900/30 text-red-300 border-red-700/40" },
};
const NEXT_STATUSES = ["contattato", "in_trattativa", "chiuso", "perso"];

export default function CrmOwnerLeads() {
    const [tab, setTab] = useState("");
    const [data, setData] = useState({ items: [], counts: {}, credits: null });
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPackages, setShowPackages] = useState(false);
    const [successBanner, setSuccessBanner] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = tab ? { status: tab } : {};
            const r = await api.get("/owner-leads/my", { params });
            setData(r.data);
        } catch { setData({ items: [], counts: {}, credits: null }); }
        finally { setLoading(false); }
    }, [tab]);
    useEffect(() => { load(); }, [load]);

    // Check ?ol_success=xxx after Stripe redirect: poll status, then reload credits
    useEffect(() => {
        const p = new URLSearchParams(location.search);
        const sid = p.get("ol_success");
        const cancelled = p.get("ol_cancel");
        if (cancelled) {
            setSuccessBanner({ type: "warn", msg: "Pagamento annullato." });
            navigate(location.pathname, { replace: true });
            return;
        }
        if (!sid) return;
        let cancel = false;
        (async () => {
            for (let i = 0; i < 8 && !cancel; i++) {
                try {
                    const r = await api.get(`/subscription/status/${sid}`);
                    if (r.data.payment_status === "paid") {
                        setSuccessBanner({
                            type: "ok",
                            msg: `Pagamento riuscito! +${r.data.credits_added || ""} lead accreditati sul tuo saldo.`,
                        });
                        await load();
                        break;
                    }
                } catch { /* keep polling */ }
                await new Promise((res) => setTimeout(res, 1500));
            }
            navigate(location.pathname, { replace: true });
        })();
        return () => { cancel = true; };
    }, [location.search]);

    return (
        <div data-testid="crm-owner-leads" className="space-y-6">
            <div>
                <span className="label-eyebrow text-gold">VD Manager · Acquisizione</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <Home size={22} className="text-gold" /> Lead Proprietari
                </h2>
                <p className="text-ink-400 text-sm mt-1">I proprietari che ti sono stati assegnati dal form pubblico /vendi-casa nelle tue zone operative.</p>
            </div>

            {successBanner && (
                <div data-testid="ol-success-banner" className={`border p-4 flex items-start gap-3 ${successBanner.type === "ok" ? "border-green-700/40 bg-green-900/20 text-green-200" : "border-amber-700/40 bg-amber-900/20 text-amber-200"}`}>
                    {successBanner.type === "ok" ? <Sparkles size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
                    <div className="flex-1 text-sm">
                        <div>{successBanner.msg}</div>
                        {successBanner.type === "ok" && (
                            <a href="/fatture" className="text-gold underline text-xs mt-1 inline-block">Vedi la fattura in &quot;Le tue fatture&quot; →</a>
                        )}
                    </div>
                    <button onClick={() => setSuccessBanner(null)} className="text-ink-400 hover:text-ink-50"><X size={14} /></button>
                </div>
            )}

            <CreditsBar credits={data.credits} onBuyMore={() => setShowPackages(true)} />

            <div className="flex flex-wrap items-center gap-2">
                <TabPill id="" active={tab === ""} onClick={setTab}>Tutti</TabPill>
                {Object.entries(STATUS_META).map(([id, m]) => (
                    <TabPill key={id} id={id} active={tab === id} onClick={setTab} count={data.counts[id]}>{m.label}</TabPill>
                ))}
            </div>

            {loading ? (
                <div className="text-ink-400 text-sm py-12 text-center">Caricamento lead...</div>
            ) : data.items.length === 0 ? (
                <EmptyState />
            ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                    {data.items.map((l) => <LeadRow key={l.id} lead={l} onOpen={() => setSelectedId(l.id)} />)}
                </div>
            )}

            {selectedId && <LeadDetail leadId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />}
            {showPackages && <PackagesModal onClose={() => setShowPackages(false)} />}
        </div>
    );
}

function TabPill({ id, active, onClick, children, count }) {
    return (
        <button
            data-testid={`ol-tab-${id || "all"}`}
            onClick={() => onClick(id)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border ${active ? "bg-gold text-ink-950 border-gold" : "border-white/15 text-ink-300 hover:text-ink-50 hover:bg-white/5"}`}
        >
            {children}{count > 0 && <span className="ml-1.5 opacity-70">({count})</span>}
        </button>
    );
}

function CreditsBar({ credits, onBuyMore }) {
    if (!credits) return null;
    const total = credits.total_left;
    const isOut = total === 0;
    return (
        <div data-testid="ol-credits" className={`border p-4 flex items-center gap-4 flex-wrap ${isOut ? "border-red-700/40 bg-red-900/20" : "border-white/10 bg-ink-900/40"}`}>
            <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-wider text-gold mb-1">Lead disponibili questo mese</div>
                <div className="font-serif text-2xl text-ink-50">
                    {total} <span className="text-sm text-ink-400">/ {credits.free_included + credits.paid_left + credits.used_this_month}</span>
                </div>
                <div className="text-[11px] text-ink-400 mt-1">
                    Free: {credits.free_left}/{credits.free_included} · Extra acquistati: {credits.paid_left} · Consumati: {credits.used_this_month}
                </div>
            </div>
            {isOut && <div className="text-xs text-red-300 flex items-center gap-1.5"><AlertCircle size={14} /> Lead esauriti — acquista un pacchetto per continuare</div>}
            <button onClick={onBuyMore} data-testid="ol-buy-btn" className="cta-outline px-4 py-2 text-[11px] flex items-center gap-1.5">
                <ShoppingCart size={12} /> Acquista pacchetti
            </button>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="border border-dashed border-white/15 p-12 text-center bg-ink-900/20">
            <Home size={30} className="text-gold mx-auto mb-3 opacity-70" />
            <h3 className="font-serif text-lg text-ink-50 mb-2">Nessun lead ancora</h3>
            <p className="text-ink-400 text-sm max-w-md mx-auto">Quando un proprietario compilerà il form pubblico /vendi-casa dalle tue zone operative, apparirà qui e riceverai una notifica email.</p>
        </div>
    );
}

function LeadRow({ lead, onOpen }) {
    const sm = STATUS_META[lead.status] || STATUS_META.nuovo;
    return (
        <button data-testid={`ol-row-${lead.id}`} onClick={onOpen} className="w-full text-left px-4 py-4 hover:bg-white/5 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-ink-50">{lead.first_name} {lead.last_name}</div>
                <div className="text-[11px] text-ink-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span><MapPin size={10} className="inline mr-1" />{lead.city}</span>
                    <span><Home size={10} className="inline mr-1" />{lead.property_type} · {lead.surface_sqm ? `${lead.surface_sqm} m²` : "—"}</span>
                    <span><Target size={10} className="inline mr-1" />{lead.objective}</span>
                </div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${sm.cls}`}>{sm.label}</span>
            <div className="text-[10px] text-ink-500 min-w-[100px] text-right">{fmtDate(lead.created_at)}</div>
            <ChevronRight size={16} className="text-ink-500" />
        </button>
    );
}

function LeadDetail({ leadId, onClose, onChanged }) {
    const [lead, setLead] = useState(null);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [valLoading, setValLoading] = useState(false);
    const [valErr, setValErr] = useState("");
    const [valResult, setValResult] = useState(null);

    const load = useCallback(async () => {
        setErr("");
        try {
            const r = await api.get(`/owner-leads/my/${leadId}`);
            setLead(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore caricamento lead");
        }
    }, [leadId]);
    useEffect(() => { load(); }, [load]);

    const accept = async () => {
        setSaving(true);
        try { await api.post(`/owner-leads/my/${leadId}/accept`); await load(); onChanged(); }
        catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    const changeStatus = async (newStatus) => {
        setSaving(true);
        try { await api.put(`/owner-leads/my/${leadId}/status`, { status: newStatus }); await load(); onChanged(); }
        catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    const addNote = async () => {
        if (!note.trim()) return;
        setSaving(true);
        try { await api.post(`/owner-leads/my/${leadId}/notes`, { body: note.trim() }); setNote(""); await load(); }
        catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };

    const runValuation = async () => {
        setValLoading(true); setValErr(""); setValResult(null);
        try {
            const r = await api.post(`/crm/valuation/ai-from-lead/${leadId}`);
            setValResult(r.data);
            await load();
        } catch (e) {
            setValErr(e?.response?.data?.detail || "Errore durante la valutazione");
        } finally { setValLoading(false); }
    };

    return (
        <div data-testid="ol-detail" className="fixed inset-0 z-[60] flex justify-end">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-3xl bg-ink-950 border-l border-white/10 overflow-y-auto">
                <button onClick={onClose} data-testid="ol-detail-close" className="sticky top-3 float-right mr-3 z-10 text-ink-400 hover:text-ink-50 bg-ink-950/95 border border-white/10 p-1.5" aria-label="Chiudi"><X size={16} /></button>
                {err && <div className="m-6 border border-red-700/40 bg-red-900/20 text-red-300 text-sm p-3">{err}</div>}
                {!lead ? <div className="p-8 text-ink-400">Caricamento...</div> : (
                    <div className="p-6 space-y-6">
                        <div>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border inline-block ${STATUS_META[lead.status]?.cls || STATUS_META.nuovo.cls}`}>{STATUS_META[lead.status]?.label || lead.status}</span>
                            <h2 className="font-serif text-3xl text-ink-50 mt-3">{lead.first_name} {lead.last_name}</h2>
                            <div className="text-[11px] text-ink-500 mt-1">Ricevuto {fmtDate(lead.created_at)}{lead.accepted_at ? ` · Accettato ${fmtDate(lead.accepted_at)}` : ""}</div>
                        </div>

                        {/* Contact + property */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <InfoBox icon={<Phone size={12} />} label="Telefono" value={<a href={`tel:${lead.phone}`} className="text-gold underline">{lead.phone}</a>} />
                            <InfoBox icon={<Mail size={12} />} label="Email" value={<a href={`mailto:${lead.email}`} className="text-gold underline">{lead.email}</a>} />
                            <InfoBox icon={<MapPin size={12} />} label="Indirizzo" value={lead.property_address} />
                            <InfoBox icon={<MapPin size={12} />} label="Città" value={lead.city} />
                            <InfoBox icon={<Home size={12} />} label="Tipologia" value={lead.property_type} />
                            <InfoBox icon={<Ruler size={12} />} label="Metratura" value={lead.surface_sqm ? `${lead.surface_sqm} m²` : "—"} />
                            <InfoBox icon={<Target size={12} />} label="Obiettivo" value={lead.objective} />
                        </div>

                        {lead.notes && (
                            <div className="border border-white/10 p-4 bg-ink-900/40">
                                <div className="label-eyebrow mb-2">Note del proprietario</div>
                                <p className="text-sm text-ink-100 whitespace-pre-wrap">{lead.notes}</p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="border border-gold/30 bg-gold/5 p-4">
                            <div className="label-eyebrow mb-3">Azioni</div>
                            {lead.status === "assegnato" && (
                                <button data-testid="ol-accept-btn" onClick={accept} disabled={saving} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2">
                                    <Check size={13} /> Accetta lead entro 24 ore
                                </button>
                            )}
                            {lead.status !== "assegnato" && (
                                <div className="flex flex-wrap gap-2">
                                    {NEXT_STATUSES.filter((s) => s !== lead.status).map((s) => (
                                        <button key={s} data-testid={`ol-status-${s}`} onClick={() => changeStatus(s)} disabled={saving} className="cta-outline px-3 py-1.5 text-[11px]">
                                            Sposta a {STATUS_META[s].label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* AI Valutazione Rapida */}
                        <div data-testid="ol-valuation-panel" className="border border-gold/30 bg-gradient-to-br from-gold/5 to-transparent p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="label-eyebrow mb-2 flex items-center gap-2"><Sparkles size={12} className="text-gold" /> AI Valutazione di mercato</div>
                                    <p className="text-xs text-ink-400 leading-relaxed">
                                        Genera in ~30 secondi una valutazione di mercato per <strong className="text-ink-100">{lead.property_address || "questo immobile"}</strong> usando ricerca web reale + dati OMI. Il report PDF è pronto per il proprietario.
                                    </p>
                                </div>
                                <button
                                    data-testid="ol-valuation-btn"
                                    onClick={runValuation}
                                    disabled={valLoading || !lead.property_address || !lead.city || !lead.surface_sqm}
                                    title={!lead.property_address || !lead.city || !lead.surface_sqm ? "Il lead deve avere indirizzo, città e mq" : ""}
                                    className="cta-gold px-4 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-40 shrink-0"
                                >
                                    {valLoading ? <><Loader2 size={12} className="animate-spin" /> Analisi…</> : <><Calculator size={12} /> Genera Valutazione Rapida</>}
                                </button>
                            </div>
                            {(!lead.property_address || !lead.city || !lead.surface_sqm) && (
                                <div data-testid="ol-valuation-missing-data" className="mt-3 text-[11px] text-amber-300 flex items-center gap-1.5"><AlertCircle size={11} /> Servono indirizzo, città e mq per generare la valutazione</div>
                            )}
                            {valErr && <div data-testid="ol-valuation-error" className="mt-3 border border-red-700/40 bg-red-900/20 text-red-300 text-xs p-2">{valErr}</div>}
                            {valResult && (
                                <div data-testid="ol-valuation-result" className="mt-4 border border-gold/40 bg-ink-950/60 p-4 space-y-3">
                                    {!valResult.grounding?.ok && (
                                        <div data-testid="ol-valuation-grounding-warning" className="border border-amber-500/40 bg-amber-900/20 p-2 text-[11px] text-amber-100">
                                            ⚠ <strong>Grounding non disponibile</strong> — stima basata su conoscenza generale AI, non verificata sui portali. Affidabilità: bassa.
                                        </div>
                                    )}
                                    <div className="text-center">
                                        <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-1">Valutazione stimata</div>
                                        <div className="font-serif text-3xl text-gold">
                                            € {(valResult.ai_result?.price_range?.min || 0).toLocaleString("it-IT")} — € {(valResult.ai_result?.price_range?.max || 0).toLocaleString("it-IT")}
                                        </div>
                                        {valResult.ai_result?.price_range?.mid && (
                                            <div className="text-xs text-ink-100 mt-1">
                                                Centrale: <span className="text-price">€ {valResult.ai_result.price_range.mid.toLocaleString("it-IT")}</span>
                                                {valResult.ai_result.price_range.price_per_sqm_mid && <span className="text-ink-400"> · € {valResult.ai_result.price_range.price_per_sqm_mid.toLocaleString("it-IT")}/m²</span>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-widest">
                                        <span className="border border-gold/40 text-gold px-2 py-1">Affidabilità: {valResult.ai_result?.confidence || "media"}</span>
                                        {valResult.ai_result?.market_trend && <span className="border border-white/20 text-ink-100 px-2 py-1">Trend {valResult.ai_result.market_trend}</span>}
                                    </div>
                                    <div className="flex justify-center">
                                        <a
                                            data-testid="ol-valuation-pdf"
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/valuation/${valResult.id}/pdf`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="cta-gold px-4 py-2 text-[11px] inline-flex items-center gap-1.5"
                                        >
                                            <FileDown size={11} /> Scarica Report PDF
                                        </a>
                                    </div>
                                    {valResult.ai_result?.narrative && (
                                        <div className="text-xs text-ink-100 leading-relaxed border-l-2 border-gold/40 pl-3 mt-3">
                                            {valResult.ai_result.narrative.split("\n\n")[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Internal notes */}
                        <div className="border border-white/10 p-4 bg-ink-900/40">
                            <div className="label-eyebrow mb-3 flex items-center gap-2"><MessageSquare size={12} /> Note interne ({lead.internal_notes?.length || 0})</div>
                            <ul className="space-y-2 mb-3">
                                {(lead.internal_notes || []).map((n) => (
                                    <li key={n.id} className="text-sm text-ink-100 border-l-2 border-gold/40 pl-3">
                                        <div className="text-[10px] text-ink-500 mb-1">{fmtDate(n.at)} · {n.by_email || n.by}</div>
                                        {n.body}
                                    </li>
                                ))}
                                {(!lead.internal_notes || !lead.internal_notes.length) && <li className="text-xs text-ink-500 italic">Nessuna nota ancora</li>}
                            </ul>
                            <div className="flex gap-2">
                                <input data-testid="ol-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi nota interna..." className="input-vd" />
                                <button data-testid="ol-note-add" onClick={addNote} disabled={saving || !note.trim()} className="cta-gold px-4 py-2 text-[11px]">Aggiungi</button>
                            </div>
                        </div>

                        {/* History */}
                        <div>
                            <div className="label-eyebrow mb-3 flex items-center gap-2"><ClipboardList size={12} /> Storico azioni</div>
                            <ul className="space-y-1.5 text-xs text-ink-400">
                                {(lead.history || []).slice().reverse().map((h, i) => (
                                    <li key={i} className="border-l-2 border-white/10 pl-3">
                                        <span className="text-ink-100">{h.type}</span>
                                        {h.to && <span> → {h.to}</span>}
                                        {h.from && <span> (da {h.from})</span>}
                                        <span className="text-ink-500 ml-2">{fmtDate(h.at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function InfoBox({ icon, label, value }) {
    return (
        <div className="border border-white/10 bg-ink-950/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-1.5 mb-1.5">{icon} {label}</div>
            <div className="text-sm text-ink-100">{value || "—"}</div>
        </div>
    );
}

function PackagesModal({ onClose }) {
    const [pkgs, setPkgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutId, setCheckoutId] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/owner-leads/packages")
            .then((r) => setPkgs(r.data.items || []))
            .catch(() => setPkgs([]))
            .finally(() => setLoading(false));
    }, []);

    const buy = async (id) => {
        setErr("");
        setCheckoutId(id);
        try {
            const r = await api.post("/owner-leads/checkout", {
                package_id: id,
                origin_url: PUBLIC_SITE_URL,
            });
            if (r.data.url) {
                window.location.href = r.data.url;
            } else {
                setErr("URL Stripe mancante");
                setCheckoutId(null);
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore durante l'avvio del pagamento");
            setCheckoutId(null);
        }
    };

    const fmt = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

    return (
        <div data-testid="ol-packages-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-4xl bg-ink-950 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <span className="label-eyebrow text-gold">Acquista lead extra</span>
                        <h3 className="font-serif text-2xl text-ink-50 mt-1">Pacchetti Lead Proprietari</h3>
                        <p className="text-xs text-ink-400 mt-2">Accredito immediato dopo il pagamento. Fattura elettronica generata automaticamente e disponibile in &quot;Le tue fatture&quot;.</p>
                    </div>
                    <button data-testid="ol-packages-close" onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>

                {err && <div className="m-6 border border-red-700/40 bg-red-900/20 text-red-300 text-sm p-3">{err}</div>}

                <div className="p-6">
                    {loading ? (
                        <div className="text-ink-400 text-sm py-8 text-center">Caricamento pacchetti...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {pkgs.map((p) => {
                                const busy = checkoutId === p.id;
                                const featured = !!p.badge;
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`ol-pkg-${p.id}`}
                                        className={`border p-5 flex flex-col relative transition-all ${featured ? "border-gold bg-gold/5" : "border-white/10 bg-ink-900/40 hover:border-white/25"}`}
                                    >
                                        {p.badge && (
                                            <span className="absolute -top-2.5 left-4 bg-gold text-ink-950 text-[10px] uppercase tracking-wider px-2 py-0.5 font-medium">{p.badge}</span>
                                        )}
                                        <div className="font-serif text-4xl text-ink-50">{p.quantity}</div>
                                        <div className="text-xs text-ink-400 mt-1">lead proprietari</div>
                                        <div className="mt-4 font-serif text-2xl text-price">{fmt(p.price)}</div>
                                        <div className="text-[10px] text-ink-500">{p.vat_label}</div>
                                        <div className="text-[11px] text-ink-400 mt-1">{p.unit_hint}</div>
                                        <button
                                            data-testid={`ol-pkg-buy-${p.id}`}
                                            onClick={() => buy(p.id)}
                                            disabled={busy || !!checkoutId}
                                            className={`mt-5 py-2.5 text-xs flex items-center justify-center gap-2 ${featured ? "cta-gold" : "cta-outline"} disabled:opacity-50 disabled:cursor-not-allowed`}
                                        >
                                            {busy ? "Reindirizzamento…" : <><ShoppingCart size={12} /> Acquista</>}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-6 border border-white/10 bg-ink-900/30 p-4 text-[11px] text-ink-400 space-y-1">
                        <div><b className="text-ink-100">Come funziona:</b> paghi tramite Stripe (carta o Apple/Google Pay). Al buon fine, i lead vengono aggiunti immediatamente al tuo saldo e riceverai la fattura via email.</div>
                        <div>I lead extra non scadono: si consumano dopo aver esaurito i 5 gratis mensili.</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
